import { API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./apiClient";
import { AuthService } from "./authService";

export interface ProvablyFairRoundData {
  roundId: string;
  multiplier: string;
  createdAt: string;
  provablyFair: {
    clientSeed: Array<{
      seed: string;
      name: string;
      _id: string;
    }>;
    serverSeedHash: string;
    serverSeed: string;
    crashPoint: string;
    proof: string;
  };
}

const asString = (value: unknown, fallback = "N/A") => {
  if (value === undefined || value === null || value === "") return fallback;
  return String(value);
};

const normalizeRound = (round: any): ProvablyFairRoundData | null => {
  if (!round || typeof round !== "object") return null;

  const fairness =
    round.provablyFair ?? round.provably_fair ?? round.fairness ?? {};
  const rawMultiplier =
    round.multiplier?.$numberDecimal ??
    round.multiplier ??
    round.crashPoint?.$numberDecimal ??
    round.crashPoint ??
    fairness.crashPoint;
  const multiplier = Number.parseFloat(String(rawMultiplier).replace(/x$/i, ""));

  if (!Number.isFinite(multiplier)) return null;

  const rawClientSeeds = fairness.clientSeed ?? fairness.clientSeeds;
  const currentUserId = AuthService.getUserId();
  const currentUsername = AuthService.getUsername();
  const clientSeed = Array.isArray(rawClientSeeds)
    ? rawClientSeeds.map((item: any, index: number) =>
        typeof item === "string"
          ? {
              seed: item,
              name: `Player ${index + 1}`,
              _id: String(index),
            }
          : (() => {
              const itemId = asString(
                item?._id ?? item?.id ?? item?.userId,
                String(index),
              );
              return {
                seed: asString(item?.seed, ""),
                name:
                  currentUserId && itemId === currentUserId
                    ? currentUsername
                    : asString(
                        item?.name ?? item?.clientName ?? item?.userName,
                        `Player ${index + 1}`,
                      ),
                _id: itemId,
              };
            })(),
      )
    : [];

  return {
    roundId: asString(round.roundId ?? round.round ?? round.id),
    multiplier: String(rawMultiplier).replace(/x$/i, ""),
    createdAt: asString(
      round.createdAt ?? round.created_at,
      new Date().toISOString(),
    ),
    provablyFair: {
      clientSeed,
      serverSeedHash: asString(
        fairness.serverSeedHash ?? fairness.server_seed_hash,
      ),
      serverSeed: asString(fairness.serverSeed ?? fairness.server_seed),
      crashPoint: asString(
        fairness.crashPoint ?? fairness.crash_point,
        `${multiplier}x`,
      ),
      proof: asString(fairness.proof),
    },
  };
};

export const loadRoundResults = async (): Promise<ProvablyFairRoundData[]> => {
  if (!AuthService.getToken()) {
    throw new Error("Authentication token is unavailable");
  }

  const response = await apiClient.post(API_ENDPOINTS.roundResults, {
    eventId: AuthService.getEventId(),
  });
  const payload = response.data;
  const rounds: unknown[] | null = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.data?.data)
      ? payload.data.data
      : Array.isArray(payload)
        ? payload
        : null;

  if (!rounds) throw new Error("Invalid rounds response");

  return rounds
    .map(normalizeRound)
    .filter((round): round is ProvablyFairRoundData => round !== null);
};
